import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Download, CheckCircle2, Trash2, WifiOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  downloadDrillsForOffline,
  getOfflinePackInfo,
  clearOfflinePack,
  getPendingSyncCount,
  syncAll
} from "@/utils/offlineStorage";

const OfflinePackCard = () => {
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, phase: "" });
  const [info, setInfo] = useState({ cachedAt: null, drillCount: 0 });
  const [pendingSync, setPendingSync] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refresh = async () => {
    setInfo(getOfflinePackInfo());
    try {
      setPendingSync(await getPendingSyncCount());
    } catch (e) {
      setPendingSync(0);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    refresh();
  }, []);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setProgress({ current: 0, total: 0, phase: "fetching_list" });
    try {
      const token = localStorage.getItem("token");
      const result = await downloadDrillsForOffline(token, (p) => setProgress(p));
      toast.success(`Downloaded ${result.drills} drills + ${result.images} images for offline use`);
      await refresh();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Could not download offline content");
    } finally {
      setDownloading(false);
    }
  };

  const handleClear = async () => {
    try {
      await clearOfflinePack();
      toast.success("Offline content cleared");
      await refresh();
    } catch (e) {
      toast.error("Failed to clear offline content");
    }
  };

  const handleSyncNow = async () => {
    if (!navigator.onLine) {
      toast.error("You're offline. Reconnect to sync.");
      return;
    }
    setSyncing(true);
    try {
      await syncAll();
      toast.success("Sync complete");
      await refresh();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const phaseLabel = {
    fetching_list: "Fetching drill list...",
    caching_drills: "Caching drills...",
    caching_images: "Caching manga images...",
    complete: "Done!"
  }[progress.phase] || "Preparing...";

  const percent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;
  const isPackInstalled = info.drillCount > 0;

  return (
    <Card className="bg-zinc-900/60 border border-zinc-800">
      <CardHeader>
        <CardTitle className="text-white font-heading uppercase tracking-tight flex items-center gap-2">
          <WifiOff className="w-5 h-5 text-amber-500" />
          Offline Mode
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-zinc-400">
          Download all 87 drills + step-by-step manga guides to your device so you can train without internet.
          Workouts and jumps logged offline auto-sync when you reconnect.
        </p>

        {/* Pack status */}
        {isPackInstalled && !downloading && (
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 flex items-start gap-3" data-testid="offline-pack-installed">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-emerald-200">Offline pack installed</p>
              <p className="text-xs text-emerald-300/80">
                {info.drillCount} drills cached • Last updated {info.cachedAt ? new Date(info.cachedAt).toLocaleDateString() : "—"}
              </p>
            </div>
          </div>
        )}

        {/* Download progress */}
        {downloading && (
          <div className="space-y-2" data-testid="offline-download-progress">
            <p className="text-xs uppercase tracking-widest text-zinc-400">{phaseLabel}</p>
            <Progress value={percent} className="h-2" />
            <p className="text-xs text-zinc-500 text-right">
              {progress.current} / {progress.total || "?"} ({percent}%)
            </p>
          </div>
        )}

        {/* Pending sync count */}
        {pendingSync > 0 && (
          <div className="rounded-md bg-orange-500/10 border border-orange-500/30 p-3 flex items-center justify-between" data-testid="offline-pending-sync">
            <div>
              <p className="text-sm font-bold text-orange-200">{pendingSync} item{pendingSync > 1 ? "s" : ""} waiting to sync</p>
              <p className="text-xs text-orange-300/80">Workouts/jumps logged offline</p>
            </div>
            <Button
              size="sm"
              onClick={handleSyncNow}
              disabled={syncing || !navigator.onLine}
              className="bg-orange-600 hover:bg-orange-500 text-white font-bold uppercase text-xs"
              data-testid="sync-now-btn"
            >
              {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Sync Now"}
            </Button>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1 bg-orange-600 hover:bg-orange-500 text-white font-black uppercase"
            data-testid="download-offline-pack-btn"
          >
            {downloading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Downloading...</>
            ) : isPackInstalled ? (
              <><Download className="w-4 h-4 mr-2" /> Refresh Offline Pack</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Download for Offline Use</>
            )}
          </Button>
          {isPackInstalled && !downloading && (
            <Button
              onClick={handleClear}
              variant="outline"
              className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-bold uppercase"
              data-testid="clear-offline-pack-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Clear
            </Button>
          )}
        </div>

        <p className="text-[10px] text-zinc-600 text-center uppercase tracking-widest">
          Uses ~15–25 MB of device storage
        </p>
      </CardContent>
    </Card>
  );
};

export default OfflinePackCard;
